import React, { useState, useEffect } from 'react';
import { Table, Space } from 'antd';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { getColumnDefinitions } from './columnDefinitions';
import CalculatorTableControls from './CalculatorTableControls';

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const CalculatorTable = ({ data }) => {
  const [isRSUIncluded, setIsRSUIncluded] = useState(true);
  const [isAnnual, setIsAnnual] = useState(true);
  const [columns, setColumns] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const newColumns = getColumnDefinitions(isRSUIncluded, isAnnual);
    setColumns(newColumns);
  }, [isRSUIncluded, isAnnual]);

  const handleResize = (index) => (e, { size }) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: size.width,
    };
    setColumns(newColumns);
  };

  const mergeColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  const toggleRSU = () => {
    setIsRSUIncluded(!isRSUIncluded);
  };

  const toggleAnnualMonthly = (checked) => {
    setIsAnnual(checked);
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
  };

  const extractValue = (obj) => {
    if (obj === null || obj === undefined) return '';
    if (typeof obj !== 'object') return obj;
    if (obj.props && obj.props.children) {
      return extractValue(obj.props.children);
    }
    return obj;
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return '';
    
    // If it's already a number, format it
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    
    // If it's a string, try to extract a number from it
    if (typeof value === 'string') {
      // Remove currency symbols, commas, and percentage signs
      const numericValue = value.replace(/[£$,%]/g, '');
      if (!isNaN(numericValue)) {
        return parseFloat(numericValue).toFixed(2);
      }
      // If it's not a numeric string, return it as is
      return value;
    }
    
    // For any other type, convert to string
    return String(value);
  };

  const exportToCSV = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Data:', data);
      console.log('Column Definitions:', getColumnDefinitions(isRSUIncluded, isAnnual));
      console.log('Sample Row:', JSON.stringify(data[0], null, 2));
    }

    const columnDefsAnnualIncl = getColumnDefinitions(true, true);
    const columnDefsAnnualExcl = getColumnDefinitions(false, true);
    const columnDefsMonthlyIncl = getColumnDefinitions(true, false);
    const columnDefsMonthlyExcl = getColumnDefinitions(false, false);
  
    const allColumnDefs = [
      ...columnDefsAnnualIncl,
      ...columnDefsAnnualExcl.filter(col => !columnDefsAnnualIncl.find(c => c.dataIndex === col.dataIndex)),
      ...columnDefsMonthlyIncl.filter(col => !columnDefsAnnualIncl.find(c => c.dataIndex === col.dataIndex)),
      ...columnDefsMonthlyExcl.filter(col => !columnDefsAnnualIncl.find(c => c.dataIndex === col.dataIndex))
    ];
    
    if (process.env.NODE_ENV !== 'production') {
      console.log('All Column Definitions:', allColumnDefs);
    }

    const headers = allColumnDefs.map(col => {
      if (typeof col.title === 'function') {
        const titleElement = col.title();
        return titleElement.props ? titleElement.props.children[0] : titleElement;
      }
      return col.title;
    }).join(',');
  
    const csvContent = data.map((row, rowIndex) => {
      return allColumnDefs.map((col, colIndex) => {
        let value = row[col.dataIndex];
        value = extractValue(value);
        if (col.render) {
          value = col.render(value, row);
          value = extractValue(value);
        }
        value = formatValue(value);
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Row ${rowIndex}, Column ${colIndex} (${col.dataIndex}):`, value);
        }
        return `"${value}"`;
      }).join(',');
    }).join('\n');
  
    const csv = [headers, csvContent].join('\n');
  
    if (process.env.NODE_ENV !== 'production') {
      console.log('CSV Content:', csv);
    }
  
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'salary_calculator_data.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
      <CalculatorTableControls 
        isRSUIncluded={isRSUIncluded}
        toggleRSU={toggleRSU}
        isAnnual={isAnnual}
        toggleAnnualMonthly={toggleAnnualMonthly}
        exportToCSV={exportToCSV}
      />
      </Space>
      <Table 
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        bordered
        dataSource={data} 
        columns={mergeColumns} 
        scroll={{ x: 'max-content' }}
        pagination={{ 
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onShowSizeChange: handlePageSizeChange,
          showTotal: (total) => `Total ${total} items`
        }}
      />
    </div>
  );
};

export default CalculatorTable;